import styled, { css } from 'styled-components';
import { colors, gradients, gradientToCSS } from '@config/colors';
import { spacing, borderRadius } from '@config/spacing';
import { typography } from '@config/typography';
import { transitions } from '@config/constants';
import { sizes } from '@config/sizes';

interface ButtonProps {
  $fullWidth?: boolean;
}

const buttonBase = css<ButtonProps>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${spacing.sm};
  border-radius: var(--glass-radius, ${borderRadius['3xl']});
  font-family: ${typography.fontFamily.CTA};
  font-weight: ${typography.fontWeights.semibold};
  font-size: ${typography.fontSize.base};
  line-height: 1;
  padding: ${spacing.lg} ${spacing.xl};
  border: none;
  cursor: pointer;
  transition: all 200ms ${transitions.easing.easeInOut};
  ${({ $fullWidth }) => $fullWidth && 'width: 100%;'}
  text-decoration: none;
`;

export const PrimaryButton = styled.button<ButtonProps>`
  ${buttonBase};
  color: ${colors.neutral.extraLight};
  background: ${gradientToCSS(gradients.accentCoralDark)};
  box-shadow: 0 4px 16px rgba(251, 113, 133, 0.3);
  max-width: ${sizes.buttonWidthMax};
  min-width: ${sizes.buttonWidthMin};
  min-height: ${sizes.buttonLg};

  &:hover,
  &:focus-visible {
    transform: translateY(-2px) scale(1.02);
    box-shadow: 0 8px 25px rgba(239, 68, 68, 0.4);
    background: ${gradientToCSS(gradients.accentCoralDark)};
    filter: brightness(1.15);
  }

  &:active {
    transform: translateY(-1px) scale(0.98);
    transition: transform 0.1s ease-out;
  }

  &:focus-visible {
    outline: 2px solid ${colors.accent.coral.light};
    outline-offset: 2px;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
    box-shadow: 0 4px 16px rgba(251, 113, 133, 0.2);
  }
`;

export const SecondaryButton = styled.button<ButtonProps>`
  ${buttonBase};
  color: ${colors.highlight.dark};
  background: ${colors.glassSurface};
  border: 1px solid ${colors.glassBorder};
  box-shadow: 0 4px 16px ${colors.seafoamOverlay10};
  backdrop-filter: blur(20px);

  &:hover,
  &:focus-visible {
    background: ${colors.glassSurfaceHover};
    border-color: ${colors.highlight.primary};
    transform: translateY(-1px);
  }

  &:focus-visible {
    outline: 2px solid ${colors.focusRing};
    outline-offset: 2px;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

